import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, KeyRound, User, BookOpen, Eye, EyeOff } from 'lucide-react';

import caregiverService from '../../services/caregiverService';
import AuthShell from '../Auth/AuthShell';

/**
 * The sponsor a setup link from before FECOA-2156 carries in its hash
 * (`/caregiver-login?cg=…#SPONSOR`). Those links still sign in against the
 * sponsor. New links carry `?study=` instead and never a hash.
 */
export const legacySponsorFromLocation = (location = window.location) => {
  const params = new URLSearchParams(location.search);
  const hash = location.hash || '';
  if (params.get('study') || hash.length <= 1) return '';
  const raw = hash.substring(1);
  try {
    return decodeURIComponent(raw).trim();
  } catch (_) {
    return raw.trim();
  }
};

/**
 * Caregiver login screen (FECOA-1320).
 *
 * Caregivers sign in with the Study Code of the participants they care for,
 * their Caregiver ID and their PIN (FECOA-2156), the same Study Code the
 * participants use. A setup QR fills all three from `?study=`, `?cg=` and
 * `?pin=`. Setup links handed out before FECOA-2156 carry the sponsor in the
 * hash instead; those keep working with the scope field hidden, as before.
 */
const CaregiverLogin = () => {
  const navigate = useNavigate();
  const [caregiverId, setCaregiverId] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [studyCode, setStudyCode] = useState('');
  const [legacySponsorId] = useState(() => legacySponsorFromLocation());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const bySponsor = Boolean(legacySponsorId);

  useEffect(() => {
    // FECOA-1428/1430/1431: a scanned setup QR lands here with the Caregiver ID
    // (and, at creation time, the temporary PIN) in the query string. Pre-fill
    // them so the caregiver can sign in without manual entry.
    const params = new URLSearchParams(window.location.search);
    const study = params.get('study');
    const cg = params.get('cg');
    const prefillPin = params.get('pin');
    if (study) setStudyCode(study.trim());
    if (cg) setCaregiverId(cg.toUpperCase());
    if (prefillPin) setPin(prefillPin);
    // FECOA-1514 (AC5): an idle logout redirects here with ?expired=1.
    if (params.get('expired')) setSessionExpired(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!caregiverId.trim() || !pin || (!bySponsor && !studyCode.trim())) {
      setError(
        bySponsor
          ? 'Caregiver ID and PIN are both required.'
          : 'Study Code, Caregiver ID and PIN are all required.',
      );
      return;
    }
    setSubmitting(true);
    try {
      const result = await caregiverService.login({
        caregiver_id: caregiverId.trim(),
        pin,
        ...(bySponsor ? { sponsor_id: legacySponsorId } : { study_code: studyCode.trim() }),
      });
      if (result.requires_pin_change) {
        navigate('/caregiver/first-login');
      } else {
        navigate('/caregiver/dashboard');
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.error || err?.response?.data?.detail;
      if (status === 423) {
        setError(
          detail ||
            'Account locked. Please contact your study coordinator to reset your PIN.',
        );
      } else if (status === 401) {
        // A failed sign-in never says which part was wrong.
        setError(
          bySponsor ? 'Invalid Caregiver ID or PIN.' : 'Invalid Study Code, Caregiver ID or PIN.',
        );
      } else {
        setError(detail || 'Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'input h-11 pl-10 pr-3';

  // Suppress password-manager autofill/overlay icons. Caregiver ID + PIN are
  // not browser credentials, so LastPass/1Password/etc. wrongly inject the
  // saved email here. These per-manager opt-out attributes hide the icons.
  //
  // FECOA-1998: they only cover the third-party managers. Chrome's built-in
  // manager ignores autoComplete="off" on a form it has already classified as a
  // sign-in form, and that classification comes from the markup shape — a text
  // input followed by a type=password input. So the PIN below declares
  // "new-password" (the pattern already proven on CaregiverFirstLoginPin),
  // which moves the form out of the sign-in classification, and every input
  // carries an explicit name/id that does not read as a username, email or
  // password for the heuristics to latch onto.
  const noPwManager = {
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
  };

  return (
    <AuthShell
      title="Caregiver Sign In"
      subtitle={bySponsor
        ? 'Use the Caregiver ID and PIN your study coordinator gave you.'
        : 'Use the Study Code, Caregiver ID and PIN your study coordinator gave you.'}
      footer={
        <>
          Forgot your PIN?{' '}
          <span className="text-gray-700 dark:text-gray-300">Contact your study coordinator.</span>
        </>
      }
    >
        {sessionExpired && (
          <div className="mb-5 rounded-lg border border-warning-500/30 bg-warning-50 px-3.5 py-3 text-sm text-warning-800 dark:bg-warning-500/10 dark:text-warning-400">
            Your session expired due to inactivity. Please sign in again.
          </div>
        )}

        <form
          onSubmit={submit}
          className="space-y-5"
        >
          {!bySponsor && (
            <div>
              <label
                htmlFor="caregiver-study-reference"
                className="label"
              >
                Study Code
              </label>
              <div className="relative">
                <BookOpen className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  id="caregiver-study-reference"
                  name="caregiver-study-reference"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={studyCode}
                  onChange={(e) => setStudyCode(e.target.value)}
                  placeholder="Study Code"
                  {...noPwManager}
                  className={inputCls}
                />
              </div>
              <p className="hint">
                The same Study Code the participant you care for uses.
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="caregiver-reference"
              className="label"
            >
              Caregiver ID
            </label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                id="caregiver-reference"
                name="caregiver-reference"
                autoComplete="off"
                value={caregiverId}
                onChange={(e) => setCaregiverId(e.target.value.toUpperCase())}
                placeholder="CG-00123"
                {...noPwManager}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="caregiver-access-code"
              className="label"
            >
              PIN
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type={showPin ? 'text' : 'password'}
                id="caregiver-access-code"
                name="caregiver-access-code"
                autoComplete="new-password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                {...noPwManager}
                inputMode="numeric"
                className={inputCls.replace('pr-3', 'pr-11')}
              />
              <button
                type="button"
                onClick={() => setShowPin((s) => !s)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p className="rounded-lg border border-danger-500/25 bg-danger-50 px-3.5 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary btn-lg w-full"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
          </button>
        </form>
    </AuthShell>
  );
};

export default CaregiverLogin;
